/**
 * /admin/dead-letters — view + replay/delete failed pipeline events.
 *
 * Backed by `stream:<name>:dead` populated by StreamConsumer when an
 * entry exceeds maxDeliveries. Read-only by default; replay pushes the
 * entry back into the live stream, delete removes it from the dead
 * stream (no undo).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api';
import { toastError, toastInfo, toastSuccess } from '../../lib/toast';

interface DeadLetterEntry {
  id: string;
  stream: string;
  originalId: string | null;
  error: string | null;
  failedAt: string | null;
  event: string | null;
}

interface DeadLetterList {
  items: DeadLetterEntry[];
  streamLength: number;
}

const STREAMS = ['raw_messages'] as const;
type Stream = (typeof STREAMS)[number];

export function DeadLettersPage() {
  const qc = useQueryClient();
  const [stream] = useState<Stream>('raw_messages');
  const [expanded, setExpanded] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['dead-letters', stream],
    queryFn: () =>
      api.get<DeadLetterList>(
        `/api/admin/dead-letters?stream=${stream}&limit=200`,
      ),
    refetchInterval: 30_000,
  });

  const replay = useMutation({
    mutationFn: (entryId: string) =>
      api.post<{ ok: boolean; newId: string | null }>(
        `/api/admin/dead-letters/replay`,
        { stream, entryId },
      ),
    onSuccess: (r) => {
      if (r.ok) {
        toastSuccess(`已重新投递（新 ID ${r.newId ?? '?'}）`);
      } else {
        toastInfo('找不到这条记录');
      }
      qc.invalidateQueries({ queryKey: ['dead-letters', stream] });
    },
    onError: (e) => toastError(e),
  });

  const remove = useMutation({
    mutationFn: (entryId: string) =>
      api.delete<{ deleted: number }>(`/api/admin/dead-letters`, {
        stream,
        entryId,
      }),
    onSuccess: (r) => {
      if (r.deleted) toastSuccess('已删除');
      else toastInfo('没匹配的记录');
      qc.invalidateQueries({ queryKey: ['dead-letters', stream] });
    },
    onError: (e) => toastError(e),
  });

  const replayBulk = useMutation({
    mutationFn: () =>
      api.post<{ requested: number; replayed: number; failed: number }>(
        `/api/admin/dead-letters/replay-bulk`,
        { stream, limit: 50 },
      ),
    onSuccess: (r) => {
      if (r.replayed > 0) {
        toastSuccess(
          `已重投 ${r.replayed} 条（共扫描 ${r.requested} 条${r.failed ? `，${r.failed} 条失败` : ''}）`,
        );
      } else {
        toastInfo('没有可重投的记录');
      }
      qc.invalidateQueries({ queryKey: ['dead-letters', stream] });
    },
    onError: (e) => toastError(e),
  });

  const removeAll = useMutation({
    mutationFn: () =>
      api.delete<{ cleared: number }>(`/api/admin/dead-letters/all`, {
        stream,
      }),
    onSuccess: (r) => {
      if (r.cleared > 0) toastSuccess(`已清空 ${r.cleared} 条死信`);
      else toastInfo('队列本来就是空的');
      qc.invalidateQueries({ queryKey: ['dead-letters', stream] });
    },
    onError: (e) => toastError(e),
  });

  const items = list.data?.items ?? [];
  const total = list.data?.streamLength ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">死信队列</h1>
          <p className="text-sm text-slate-500 mt-1">
            Redis 流 <code className="font-mono">{`stream:${stream}:dead`}</code> ·
            总共 {total} 条 · 当前显示最新 {items.length} 条
            。当某条消息在管线里反复消费失败、超过最大重试次数后，会被丢到这里；原流立即 ack 不再重试。
          </p>
        </div>
        <div className="flex gap-2 whitespace-nowrap">
          <button
            className="btn-secondary text-xs"
            disabled={replayBulk.isPending || total === 0}
            title="把最新 50 条死信重新投回原流；不会从死信队列删除，方便核对"
            onClick={() => {
              if (confirm(`确定要把最新 ${Math.min(50, total)} 条死信重新投回原流？\n\n重投不会从死信队列删除原记录。`)) {
                replayBulk.mutate();
              }
            }}
          >
            {replayBulk.isPending ? '重投中…' : `↻ 一键重投（最新 ${Math.min(50, total)} 条）`}
          </button>
          <button
            className="btn-danger text-xs"
            disabled={removeAll.isPending || total === 0}
            title="清空整个死信队列（不可恢复）"
            onClick={() => {
              if (
                confirm(
                  `确定要清空全部 ${total} 条死信？\n\n不可恢复。建议先点"一键重投"确认问题不会复发，再清空。`,
                )
              ) {
                removeAll.mutate();
              }
            }}
          >
            {removeAll.isPending ? '清空中…' : `🗑 清空全部（${total} 条）`}
          </button>
        </div>
      </div>

      <div className="card overflow-x-auto">
        {list.isLoading ? (
          <div className="text-sm text-slate-500">加载中…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500">
            干净 — 当前没有死信。
          </div>
        ) : (
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500 uppercase border-b">
                <th className="py-2 pr-3">死信条目 ID</th>
                <th className="py-2 pr-3">原条目 ID</th>
                <th className="py-2 pr-3">失败时间</th>
                <th className="py-2 pr-3">错误信息</th>
                <th className="py-2 pr-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <>
                  <tr
                    key={e.id}
                    className="border-b last:border-0 cursor-pointer hover:bg-slate-50"
                    onClick={() =>
                      setExpanded((cur) => (cur === e.id ? null : e.id))
                    }
                  >
                    <td className="py-2 pr-3 font-mono">{e.id}</td>
                    <td className="py-2 pr-3 font-mono text-slate-500">
                      {e.originalId ?? '—'}
                    </td>
                    <td className="py-2 pr-3 text-slate-500">
                      {e.failedAt
                        ? new Date(e.failedAt).toLocaleString()
                        : '—'}
                    </td>
                    <td
                      className="py-2 pr-3 max-w-[24rem] truncate text-red-700"
                      title={e.error ?? ''}
                    >
                      {e.error ?? '—'}
                    </td>
                    <td className="py-2 pr-3 space-x-2">
                      <button
                        className="btn-secondary text-xs"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          replay.mutate(e.id);
                        }}
                        disabled={replay.isPending}
                      >
                        重投
                      </button>
                      <button
                        className="btn-danger text-xs"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          if (confirm('从死信删除（不可撤销）？')) {
                            remove.mutate(e.id);
                          }
                        }}
                        disabled={remove.isPending}
                      >
                        删除
                      </button>
                    </td>
                  </tr>
                  {expanded === e.id && (
                    <tr key={`${e.id}-expanded`} className="bg-slate-50">
                      <td colSpan={5} className="py-2 px-3">
                        <pre className="text-[11px] whitespace-pre-wrap break-words bg-white border rounded p-2 max-h-64 overflow-y-auto">
                          {tryFormatJson(e.event)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function tryFormatJson(raw: string | null): string {
  if (!raw) return '(no event payload)';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
